import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import LicenseContext from 'src/contexts/LicenseContext';
import { DriverEntity } from 'src/entities/driverEntity';
import { DesignatedTruckFeature } from 'src/entities/orderEntity';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { TruckEntity, TruckRequestEntity } from 'src/entities/truckEntity';
import { useGaragesQuery } from 'src/hooks/useGarages.query';
import { useMutationPartnerCompanies, usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';
import { useMutationTruck } from 'src/hooks/useQueryTrucks';
import { useTruckQuery } from 'src/hooks/useTrucks.query';
import { queryKey } from 'src/hooks/useTrucks.request';

import Details from './Details';
import { schema } from './schema';
import SelectDriverDialog from './SelectDriverDialog';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  updateIsLoading?: (isLoading: boolean) => void;
  editEntity?: TruckEntity;
  truckId?: number;
}

const FormDialog: FC<Props> = memo((
  {
    isOpen,
    onClose,
    updateIsLoading,
    editEntity: entity,
    truckId,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: truckData } = useTruckQuery(truckId);
  const [editEntity, setEditEntity] = useState<TruckEntity>();
  const [features, setFeatures] = useState<DesignatedTruckFeature[]>([]);
  const [companyId, setCompanyId] = useState<number>(0);

  useEffect(() => {
    if (entity) {
      setEditEntity(entity);
    } else if (truckData) {
      setEditEntity(truckData);
    }
  }, [entity, truckData]);

  const form = useForm<TruckEntity>({
    resolver: yupResolver(schema)
  });

  const {
    control,
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = form;

  const { data: partners } = usePartnerCompaniesRequest(0, '', '', isOpen, Number.MAX_SAFE_INTEGER);
  const garages = useGaragesQuery();
  const { addTruck, updateTruck } = useMutationTruck();
  const { addPartnerCompany } = useMutationPartnerCompanies();

  const [searchPartnerCompanyQuery, setSearchPartnerCompanyQuery] = useState('');
  const [selectedPartnerCompanyId, setSelectedPartnerCompanyId] = useState<number>(null);
  const [selectedPartnerCompanyName, setSelectedPartnerCompanyName] = useState<string>(editEntity?.partner_company_name);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTemporary, setIsTemporary] = useState(false);
  const [selectDriverDialogIsOpen, setSelectDriverDialogIsOpen] = useState<boolean>(false);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [selectedDriverName, setSelectedDriverName] = useState<string | undefined>(undefined);

  const openSelectDriverDialog = () => setSelectDriverDialogIsOpen(true);
  const closeSelectDriverDialog = () => setSelectDriverDialogIsOpen(false);

  const updateSelectedDriver = (maybeDriver: DriverEntity | null) => {
    setSelectedDriverId(maybeDriver?.id ?? null);
    setSelectedDriverName(maybeDriver?.name);
    setValue('default_driver_id', maybeDriver?.id ?? null);
  };

  const onChangeCompanyId = (value: number) => {
    setCompanyId(value);
    // truck の実会社は garage 由来。companyId だけ変えて garage_id が旧会社のままだと、
    // 新会社のドライバーを選べるのに保存時 truck は旧会社扱いになり validation で 400 になる。
    // garage_id をリセットして新会社の車庫を選び直させ、companyId と garage.company を一致させる。
    setValue('garage_id', '');
    // 別事業所のドライバーが残らないよう通常ドライバー選択もリセット
    setSelectedDriverId(null);
    setSelectedDriverName(undefined);
    setValue('default_driver_id', null);
  };

  const onSubmitForm: SubmitHandler<TruckEntity | TruckRequestEntity> = async (
    data
  ): Promise<void> => {
    // 並び順は画面から扱わないため送信 body から除外する（サーバ側の既存値は維持される）。
    const { sort_order: _sortOrder, ...body } = data;
    try {
      if ('id' in body) {
        await updateTruck.mutateAsync(body as TruckEntity);
      } else {
        await addTruck.mutateAsync(body as TruckRequestEntity);
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-argument
      error.response.data.map((str) => enqueueSnackbar(str));
    } finally {
      void queryClient.invalidateQueries(['useQueryPlanningsTrucks']);
      void queryClient.invalidateQueries([queryKey]).finally(() => {
        if (updateIsLoading) { updateIsLoading(false); }
        onClose();
      });
    }
  };

  const haveSelectableCompanies = licenseContext?.config?.selectable_companies?.length > 1;
  const isNew = !editEntity;

  useEffect(() => {
    if (!editEntity) {
      reset({});
      setSelectedDriverId(null);
      setSelectedDriverName(undefined);
      return;
    }

    reset(editEntity);
    setCompanyId(editEntity.company_id);
    setFeatures(editEntity.features);
    setSelectedDriverId(editEntity.default_driver_id ?? null);
    setSelectedDriverName(editEntity.default_driver_name);
  }, [editEntity, reset]);

  useEffect(() => {
    if (!licenseContext?.config?.selected_company_id) return;
    if (licenseContext?.config?.selected_company_id === 0) {
      setCompanyId(
        0
      );
      return;
    }

    setCompanyId(
      licenseContext?.config?.selected_company_id
    );
  }, [licenseContext?.config?.selected_company_id]);

  useEffect(() => {
    if (!editEntity) return;

    setSelectedPartnerCompanyId(editEntity.partner_company_id);
    setSelectedPartnerCompanyName(editEntity.partner_company_name);
    setIsTemporary(editEntity.temporary || false);
  }, [editEntity]);

  const handlePartnerCompanyOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePartnerCopmanyClose = () => {
    setAnchorEl(null);
  };

  const handlePartnerCompanySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPartnerCompanyQuery(event.target.value);
  };

  const handlePartnerCompanyClick = (id: number | null) => {
    setSelectedPartnerCompanyId(id);
    setValue('partner_company_id', id);
    if (id !== null) {
      const selected = partners.data.find((it) => it.id === id);
      setSelectedPartnerCompanyName(selected.name);
    } else {
      setSelectedPartnerCompanyName(null);
    }
    setAnchorEl(null);
  };

  const handleCreatePartnerCompany = async () => {
    if (!searchPartnerCompanyQuery) return;
    await addPartnerCompany.mutateAsync({
      name: searchPartnerCompanyQuery,
    }, {
      onSuccess: (response: AxiosResponse<PartnerCompanyEntity>) => {
        setValue('partner_company_id', response.data.id);
        setSelectedPartnerCompanyId(response.data.id);
        setSelectedPartnerCompanyName(response.data.name);
        setAnchorEl(null);
      }
    });
  };

  const filteredPartners = partners?.data.filter((partner) => partner.name.toLowerCase().includes(searchPartnerCompanyQuery.toLowerCase()));

  if (!garages.data || !partners) return null;

  return (
    <>
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <DialogTitle>トラック追加</DialogTitle>
      <DialogContent>
        <Stack component="form" noValidate spacing={2}>
          {[haveSelectableCompanies, isNew].every((bool) => bool) && (
            <>
              <InputLabel>事業所</InputLabel>
              <Select
                id="company_id"
                margin="dense"
                label="事業所"
                type="number"
                fullWidth
                variant="standard"
                value={`${companyId}`}
                onChange={(e) => onChangeCompanyId(Number(e.target.value))}
                disabled={!haveSelectableCompanies}
              >
                {licenseContext.config.selectable_companies.map((company) => (
                  <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                ))}
              </Select>
            </>
          )}
          <Stack>
            <InputLabel id="garage_id">車庫</InputLabel>
            <Controller
              control={control}
              name="garage_id"
              defaultValue=""
              render={({ field }) => (
                <Select
                  required
                  margin="dense"
                  id="garage_id"
                  label="車庫"
                  type="text"
                  fullWidth
                  variant="standard"
                  {...field}
                >
                  {garages.data.filter((it) => it.companyId === companyId).map((garage) => (
                    <MenuItem key={garage.id} value={garage.id}>
                      {garage.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText error>{errors.garage_id?.message}</FormHelperText>
          </Stack>
          <Stack direction="row" gap={1}>
            {(isTemporary) ? (
              <TextField
                required
                label="トラック"
                type="text"
                fullWidth
                variant="standard"
                disabled={isTemporary}
                InputLabelProps={{ shrink: true }}
                value="指定なし"
              />
            ) : (
              <TextField
                required
                id="license_plate_value"
                label="トラック"
                type="text"
                fullWidth
                variant="standard"
                disabled={isTemporary}
                {...register('license_plate_value')}
                error={'license_plate_value' in errors}
                helperText={errors.license_plate_value?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
            {(!isTemporary) && (
              <>
                <TextField
                  id="truck_number"
                  label="車番"
                  type="text"
                  fullWidth
                  variant="standard"
                  {...register('truck_number')}
                  error={'truck_number' in errors}
                  helperText={errors.truck_number?.message}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Stack>
          {(!isTemporary) && (
            <Stack direction="row" gap={1}>
              <Box sx={{ borderRadius: '8px', backgroundColor: '#fff' }}>
                <InputLabel>運送会社</InputLabel>
                <Button
                  variant="outlined"
                  onClick={handlePartnerCompanyOpen}
                  sx={{ textTransform: 'none', width: 200 }}
                >
                  {selectedPartnerCompanyName ?? '運送会社を選択'}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handlePartnerCopmanyClose}
                  PaperProps={{
                    style: { width: 250, padding: 0, margin: 0, },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="検索"
                    value={searchPartnerCompanyQuery}
                    onChange={handlePartnerCompanySearchChange}
                  />
                  <List sx={{ p: 0, m: 0, }}>
                    <ListItemButton
                      key={0}
                      onClick={() => handlePartnerCompanyClick(null)}
                    >
                      <ListItemText
                        primary="&nbsp;"
                      />
                    </ListItemButton>
                    {filteredPartners.map((partner) => (
                      <ListItemButton
                        key={partner.id}
                        onClick={() => handlePartnerCompanyClick(partner.id)}
                        selected={selectedPartnerCompanyId === partner.id}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <ListItemText
                          primary={partner.name}
                          secondary={partner.memo}
                          primaryTypographyProps={{ fontSize: 14, color: 'black', }}
                          secondaryTypographyProps={{ fontSize: 12, }}
                        />
                      </ListItemButton>
                    ))}
                    {searchPartnerCompanyQuery && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                        <MenuItem onClick={handleCreatePartnerCompany}>
                          <ListItemText primary={`"${searchPartnerCompanyQuery}" を新規作成`} />
                        </MenuItem>
                      </>
                    )}
                  </List>
                </Menu>
              </Box>
            </Stack>
          )}
          {(!isTemporary) && (
            <Stack direction="row" gap={1}>
              <Stack>
                <InputLabel>通常ドライバー</InputLabel>
                <Button
                  onClick={openSelectDriverDialog}
                  disabled={companyId === 0}
                >
                  {selectedDriverName ?? '未設定'}
                </Button>
              </Stack>
            </Stack>
          )}
          <Details<TruckEntity>
            form={form}
            control={control}
            register={register}
            errors={errors}
            unit={licenseContext.config?.unit}
            unitUsedForCalculation={licenseContext.config?.unit_used_for_calculation}
            features={features}
            setFeatures={setFeatures}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handleSubmit(onSubmitForm)} variant="contained">
          保存する
        </Button>
      </DialogActions>
    </Dialog>
    {selectDriverDialogIsOpen && (
      <SelectDriverDialog
        open={selectDriverDialogIsOpen}
        onClose={closeSelectDriverDialog}
        onSelect={updateSelectedDriver}
        currentDriverId={selectedDriverId ?? undefined}
        selectedCompanyId={companyId}
      />
    )}
    </>
  );
});

export default FormDialog;
